import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Reads the `?spotlight=<data-testid>` query param on every navigation and,
 * if present, scrolls the matching element into view and applies a 3-second
 * brand-teal pulse highlight to draw the eye.
 *
 * Usage: mount once at the AppShell level. Help-page items use this by
 * deep-linking to e.g. `/app/expenses?spotlight=add-expense-button`.
 *
 * Why a query param and not a hash? Routes already use hashes for tabs
 * elsewhere, and query params survive React Router's `useNavigate({replace})`
 * cleanly without colliding with anchor links.
 */
export default function useDeepLinkSpotlight() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get("spotlight");
    if (!target) return;

    // Allow the destination page time to render before we hunt for the
    // target. Two retries cover the case where the target is inside a
    // dropdown / dialog that mounts asynchronously.
    let attempts = 0;
    const tryHighlight = () => {
      attempts += 1;
      const el = document.querySelector(`[data-testid="${CSS.escape(target)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("cpc-spotlight");
        // Strip the class once the animation finishes so re-renders don't
        // leave a permanent outline.
        setTimeout(() => el.classList.remove("cpc-spotlight"), 3500);
        // Clean the URL so refresh doesn't re-fire the spotlight.
        params.delete("spotlight");
        const cleaned = params.toString();
        navigate({ pathname: location.pathname, search: cleaned ? `?${cleaned}` : "" }, { replace: true });
      } else if (attempts < 6) {
        setTimeout(tryHighlight, 250);
      }
    };
    const timer = setTimeout(tryHighlight, 200);
    return () => clearTimeout(timer);
  }, [location.search, location.pathname, navigate]);
}
